import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { 
  ArrowLeft, Users, MessageSquare, Star, Clock, Gift,
  CheckCircle, XCircle, Plus, RefreshCw, TrendingUp,
  AlertTriangle, Eye, Folder, FolderPlus, ChevronRight, BarChart3,
  Key, Copy, Trash2, FileText, Activity
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Survey Analytics Card Component
function SurveyAnalyticsCard({ title, data, labels }) {
  const total = Object.values(data || {}).reduce((sum, count) => sum + count, 0);
  if (total === 0) return null;
  
  const sortedEntries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  
  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
      <h4 className="font-bold mb-3 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-indigo-500" />
        {title}
      </h4>
      <div className="space-y-2">
        {sortedEntries.map(([key, count]) => {
          const percentage = Math.round((count / total) * 100);
          return (
            <div key={key} className="flex items-center gap-3">
              <div className="w-32 text-sm truncate">{labels[key] || key}</div>
              <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                <div 
                  className="h-full bg-indigo-500 rounded-full transition-all flex items-center justify-end pr-2"
                  style={{ width: `${Math.max(percentage, 8)}%` }}
                >
                  <span className="text-xs text-white font-bold">{count}</span>
                </div>
              </div>
              <div className="w-12 text-right text-sm text-slate-600">{percentage}%</div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-2">{total} responses</p>
    </div>
  );
}

export default function AdminBetaDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('testers');
  const [testers, setTesters] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [categorySuggestions, setCategorySuggestions] = useState({ pending: [], approved: [], rejected: [] });
  // New unified beta code system
  const [betaCodes, setBetaCodes] = useState([]);
  const [betaParticipants, setBetaParticipants] = useState([]);
  const [betaAuditLogs, setBetaAuditLogs] = useState([]);
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [newCodeValue, setNewCodeValue] = useState('');
  const [newCodeNotes, setNewCodeNotes] = useState('');

  // Wait for auth check to complete
  useEffect(() => {
    const token = localStorage.getItem('token');
    // If no token, we know user is not logged in
    if (!token) {
      setLoading(false);
      return;
    }
    // If we have a user object, auth check is complete
    if (user !== null) {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [testersRes, feedbacksRes, statsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/api/admin/beta/testers`, config),
        axios.get(`${API}/api/admin/beta/feedbacks`, config),
        axios.get(`${API}/api/admin/beta/stats`, config),
        axios.get(`${API}/api/admin/categories/all`, config).catch(() => ({ data: { pending: [], approved: [], rejected: [] } }))
      ]);
      
      // Fetch new beta code system data
      const [codesRes, participantsRes, auditRes] = await Promise.all([
        axios.get(`${API}/api/admin/beta/codes`, config).catch(() => ({ data: { codes: [] } })),
        axios.get(`${API}/api/admin/beta/participants`, config).catch(() => ({ data: { participants: [] } })),
        axios.get(`${API}/api/admin/beta/audit?limit=50`, config).catch(() => ({ data: { logs: [] } }))
      ]);
      
      setTesters(testersRes.data.testers);
      setSummary(testersRes.data.summary);
      setFeedbacks(feedbacksRes.data);
      setStats(statsRes.data);
      setCategorySuggestions(categoriesRes.data);
      setBetaCodes(codesRes.data.codes || []);
      setBetaParticipants(participantsRes.data.participants || []);
      setBetaAuditLogs(auditRes.data.logs || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setDataLoading(false);
    }
  };

  const handleExtend = async (userId, months = 3) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/beta/extend/${userId}?months=${months}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Extended access by ${months} months`);
      fetchData();
    } catch (error) {
      toast.error('Failed to extend access');
    }
  };

  const handleRevoke = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke this user\'s beta access?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/beta/revoke/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Access revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const handleApproveCategory = async (suggestionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/categories/approve/${suggestionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category approved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve category');
    }
  };

  const handleRejectCategory = async (suggestionId) => {
    if (!window.confirm('Are you sure you want to reject this category suggestion?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/categories/reject/${suggestionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject category');
    }
  };

  // ====== BETA CODE MANAGEMENT HANDLERS ======
  
  const handleCreateBetaCode = async (isCustom = false) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        notes: newCodeNotes || undefined
      };
      if (isCustom && newCodeValue.trim()) {
        payload.code = newCodeValue.trim().toUpperCase();
      }
      
      const res = await axios.post(`${API}/api/admin/beta/codes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Beta code created: ${res.data.code}`);
      setShowCreateCode(false);
      setNewCodeValue('');
      setNewCodeNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create code');
    }
  };

  const handleDeactivateCode = async (codeId, codeName) => {
    if (!window.confirm(`Deactivate code ${codeName}? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/beta/codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleMigrateKindFamily = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/beta/migrate-kindfamily`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('KINDFAMILY2025 migrated successfully');
      fetchData();
    } catch (error) {
      toast.info(error.response?.data?.message || 'Migration already completed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Admin Access Required</h1>
          <Button onClick={() => navigate('/home')}>Go Home</Button>
        </div>
      </div>
    );
  }

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      completed: 'bg-blue-100 text-blue-700 border-blue-300',
      expired: 'bg-amber-100 text-amber-700 border-amber-300',
      revoked: 'bg-red-100 text-red-700 border-red-300'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || 'bg-slate-100'}`}>
        {status}
      </span>
    );
  };

  const getCodeBadge = (code) => {
    const styles = {
      KINDFRIEND2025: 'bg-purple-100 text-purple-700',
      KINDFAMILY2025: 'bg-pink-100 text-pink-700',
      KINDBETA2025: 'bg-indigo-100 text-indigo-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[code] || 'bg-slate-100'}`}>
        {code}
      </span>
    );
  };

  const getLevelLabel = (level) => {
    const labels = {
      1: 'Main Category',
      2: 'Subcategory',
      3: 'Sub-subcategory'
    };
    return labels[level] || `Level ${level}`;
  };

  const pendingCategoryCount = categorySuggestions.pending?.length || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-black sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sidebar />
            <h1 className="text-xl font-bold">Admin Dashboard</h1>
          </div>
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-2 border-black rounded-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm">Total Testers</span>
              </div>
              <p className="text-3xl font-bold">{stats.total_testers}</p>
            </div>
            <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
              <div className="flex items-center gap-2 text-emerald-600 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Active</span>
              </div>
              <p className="text-3xl font-bold text-emerald-600">{stats.active_testers}</p>
            </div>
            <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
              <div className="flex items-center gap-2 text-indigo-600 mb-1">
                <MessageSquare className="w-4 h-4" />
                <span className="text-sm">Feedbacks</span>
              </div>
              <p className="text-3xl font-bold text-indigo-600">{stats.total_feedbacks}</p>
            </div>
            <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
              <div className="flex items-center gap-2 text-yellow-600 mb-1">
                <Star className="w-4 h-4" />
                <span className="text-sm">Avg Rating</span>
              </div>
              <p className="text-3xl font-bold text-yellow-600">{stats.average_rating.toFixed(1)}</p>
            </div>
            <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
              <div className="flex items-center gap-2 text-amber-600 mb-1">
                <FolderPlus className="w-4 h-4" />
                <span className="text-sm">Category Ideas</span>
              </div>
              <p className="text-3xl font-bold text-amber-600">{pendingCategoryCount}</p>
            </div>
          </div>
        )}

        {/* Code Breakdown */}
        {stats && (
          <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal mb-6">
            <h3 className="font-bold mb-3">By Promo Code</h3>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(stats.by_code).map(([code, data]) => (
                <div key={code} className="p-3 bg-slate-50 rounded-lg">
                  {getCodeBadge(code)}
                  <div className="mt-2 flex justify-between text-sm">
                    <span>{data.testers} testers</span>
                    <span>{data.feedbacks} feedbacks</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <Button
            variant={activeTab === 'testers' ? 'default' : 'outline'}
            onClick={() => setActiveTab('testers')}
            className={`rounded-full border-2 border-black ${activeTab === 'testers' ? 'bg-indigo-500 text-white' : ''}`}
          >
            <Users className="w-4 h-4 mr-2" /> Testers ({testers.length})
          </Button>
          <Button
            variant={activeTab === 'feedbacks' ? 'default' : 'outline'}
            onClick={() => setActiveTab('feedbacks')}
            className={`rounded-full border-2 border-black ${activeTab === 'feedbacks' ? 'bg-indigo-500 text-white' : ''}`}
          >
            <MessageSquare className="w-4 h-4 mr-2" /> Feedbacks ({feedbacks.length})
          </Button>
          <Button
            variant={activeTab === 'analytics' ? 'default' : 'outline'}
            onClick={() => setActiveTab('analytics')}
            className={`rounded-full border-2 border-black ${activeTab === 'analytics' ? 'bg-indigo-500 text-white' : ''}`}
          >
            <TrendingUp className="w-4 h-4 mr-2" /> Survey Insights
          </Button>
          <Button
            variant={activeTab === 'categories' ? 'default' : 'outline'}
            onClick={() => setActiveTab('categories')}
            className={`rounded-full border-2 border-black ${activeTab === 'categories' ? 'bg-indigo-500 text-white' : ''}`}
          >
            <Folder className="w-4 h-4 mr-2" /> 
            Categories
            {pendingCategoryCount > 0 && (
              <span className="ml-2 bg-amber-400 text-black px-2 py-0.5 rounded-full text-xs font-bold">
                {pendingCategoryCount}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'betacodes' ? 'default' : 'outline'}
            onClick={() => setActiveTab('betacodes')}
            className={`rounded-full border-2 border-black ${activeTab === 'betacodes' ? 'bg-indigo-500 text-white' : ''}`}
          >
            <Key className="w-4 h-4 mr-2" /> 
            Beta Codes ({betaCodes.length})
          </Button>
          <Button
            variant={activeTab === 'participants' ? 'default' : 'outline'}
            onClick={() => setActiveTab('participants')}
            className={`rounded-full border-2 border-black ${activeTab === 'participants' ? 'bg-indigo-500 text-white' : ''}`}
          >
            <Activity className="w-4 h-4 mr-2" /> 
            Participants ({betaParticipants.length})
          </Button>
          <Button
            variant={activeTab === 'auditlog' ? 'default' : 'outline'}
            onClick={() => setActiveTab('auditlog')}
            className={`rounded-full border-2 border-black ${activeTab === 'auditlog' ? 'bg-indigo-500 text-white' : ''}`}
          >
            <FileText className="w-4 h-4 mr-2" /> 
            Audit Log
          </Button>
        </div>

        {/* Survey Analytics Tab */}
        {activeTab === 'analytics' && stats?.survey_analytics && (
          <div className="space-y-6">
            <SurveyAnalyticsCard 
              title="How easy was navigation?" 
              questionId="ease_of_use"
              data={stats.survey_analytics.ease_of_use}
              labels={{
                very_easy: '😊 Very easy',
                somewhat_easy: '🤔 Somewhat easy',
                neutral: '😐 Neutral',
                difficult: '😕 Difficult'
              }}
            />
            <SurveyAnalyticsCard 
              title="Would recommend to a friend?" 
              questionId="would_recommend"
              data={stats.survey_analytics.would_recommend}
              labels={{
                definitely: '🔥 Definitely',
                probably: '👍 Probably',
                not_sure: '🤷 Not sure',
                probably_not: '👎 Probably not'
              }}
            />
            <SurveyAnalyticsCard 
              title="First impression" 
              questionId="first_impression"
              data={stats.survey_analytics.first_impression}
              labels={{
                excited: '🤩 Excited',
                curious: '🧐 Curious',
                confused: '😵 Confused',
                underwhelmed: '😴 Underwhelmed'
              }}
            />
            <SurveyAnalyticsCard 
              title="What would make them use LikeKind regularly?" 
              questionId="value_proposition"
              data={stats.survey_analytics.value_proposition}
              labels={{
                more_activities: '📍 More activities',
                know_people: '👥 Know people on it',
                specific_interest: '🎯 Specific interest',
                events: '🎉 Local events',
                other: '💭 Other'
              }}
            />
            <SurveyAnalyticsCard 
              title="Most requested feature" 
              questionId="missing_feature"
              data={stats.survey_analytics.missing_feature}
              labels={{
                messaging: '💬 Direct messaging',
                events_calendar: '📅 Events calendar',
                notifications: '🔔 Better notifications',
                profiles: '👤 Detailed profiles',
                search: '🔍 Better search',
                other: '💭 Other'
              }}
            />
            {Object.keys(stats.survey_analytics.ease_of_use || {}).length === 0 && (
              <div className="bg-white border-2 border-black rounded-xl p-8 text-center text-slate-500 shadow-brutal">
                No survey responses yet. Analytics will appear after testers submit feedback.
              </div>
            )}
          </div>
        )}

        {/* Testers Tab */}
        {activeTab === 'testers' && (
          <div className="bg-white border-2 border-black rounded-xl shadow-brutal overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100 border-b-2 border-black">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-bold">User</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Code</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Feedbacks</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Days Left</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {testers.map((tester) => (
                    <tr key={tester.beta_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{tester.user_name}</p>
                          <p className="text-xs text-slate-500">{tester.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">{getCodeBadge(tester.promo_code)}</td>
                      <td className="px-4 py-3">{getStatusBadge(tester.status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{tester.feedback_count}</span>
                          <span className="text-xs text-slate-500">
                            ({tester.feedbacks_this_month} this month)
                          </span>
                        </div>
                        {tester.feedbacks_this_month < (tester.feedbacks_required_monthly || 1) && tester.status === 'active' && (
                          <span className="text-xs text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Behind
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-medium ${tester.days_remaining < 14 ? 'text-amber-600' : 'text-slate-700'}`}>
                          {tester.days_remaining} days
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExtend(tester.user_id, 3)}
                            className="text-xs border border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                          >
                            <Plus className="w-3 h-3 mr-1" /> 3mo
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRevoke(tester.user_id)}
                            className="text-xs border border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {testers.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                No beta testers yet. Share your promo codes!
              </div>
            )}
          </div>
        )}

        {/* Feedbacks Tab */}
        {activeTab === 'feedbacks' && (
          <div className="space-y-4">
            {feedbacks.map((fb) => (
              <div key={fb.feedback_id} className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-bold">{fb.user_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getCodeBadge(fb.promo_code)}
                      <span className="text-xs text-slate-500">
                        {new Date(fb.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(fb.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-emerald-700 mb-1">✅ What&apos;s Working:</p>
                    <p className="text-slate-700 bg-emerald-50 p-2 rounded-lg">{fb.whats_working}</p>
                  </div>
                  <div>
                    <p className="font-medium text-red-700 mb-1">❌ What&apos;s Not Working:</p>
                    <p className="text-slate-700 bg-red-50 p-2 rounded-lg">{fb.whats_not_working}</p>
                  </div>
                  {fb.ideas && (
                    <div>
                      <p className="font-medium text-indigo-700 mb-1">💡 Ideas:</p>
                      <p className="text-slate-700 bg-indigo-50 p-2 rounded-lg">{fb.ideas}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {feedbacks.length === 0 && (
              <div className="bg-white border-2 border-black rounded-xl p-8 text-center text-slate-500 shadow-brutal">
                No feedbacks yet
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            {/* Pending Suggestions */}
            <div className="bg-white border-2 border-black rounded-xl shadow-brutal overflow-hidden">
              <div className="bg-amber-100 px-4 py-3 border-b-2 border-black flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Pending Review ({categorySuggestions.pending?.length || 0})
                </h3>
              </div>
              {categorySuggestions.pending?.length > 0 ? (
                <div className="divide-y divide-slate-200">
                  {categorySuggestions.pending.map((sugg) => (
                    <div key={sugg.suggestion_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{sugg.suggested_icon}</span>
                          <span className="font-bold text-lg">{sugg.suggested_name}</span>
                          <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full">
                            {getLevelLabel(sugg.level)}
                          </span>
                        </div>
                        {sugg.parent_category_id && (
                          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                            <ChevronRight className="w-4 h-4" />
                            Under: {sugg.parent_category_id}
                            {sugg.parent_subcategory_id && ` → ${sugg.parent_subcategory_id}`}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          Submitted: {new Date(sugg.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApproveCategory(sugg.suggestion_id)}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white border border-black"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectCategory(sugg.suggestion_id)}
                          className="border border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">
                  No pending category suggestions
                </div>
              )}
            </div>

            {/* Approved Categories */}
            <div className="bg-white border-2 border-black rounded-xl shadow-brutal overflow-hidden">
              <div className="bg-emerald-100 px-4 py-3 border-b-2 border-black">
                <h3 className="font-bold flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Approved ({categorySuggestions.approved?.length || 0})
                </h3>
              </div>
              {categorySuggestions.approved?.length > 0 ? (
                <div className="p-4 flex flex-wrap gap-2">
                  {categorySuggestions.approved.map((sugg) => (
                    <div key={sugg.suggestion_id} className="bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2 flex items-center gap-2">
                      <span>{sugg.suggested_icon}</span>
                      <span className="font-medium">{sugg.suggested_name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-slate-500 text-sm">
                  No approved categories yet
                </div>
              )}
            </div>

            {/* Rejected Categories */}
            {categorySuggestions.rejected?.length > 0 && (
              <div className="bg-white border-2 border-black rounded-xl shadow-brutal overflow-hidden">
                <div className="bg-red-100 px-4 py-3 border-b-2 border-black">
                  <h3 className="font-bold flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    Rejected ({categorySuggestions.rejected?.length || 0})
                  </h3>
                </div>
                <div className="p-4 flex flex-wrap gap-2">
                  {categorySuggestions.rejected.map((sugg) => (
                    <div key={sugg.suggestion_id} className="bg-red-50 border border-red-200 rounded-full px-4 py-2 flex items-center gap-2 opacity-60">
                      <span>{sugg.suggested_icon}</span>
                      <span className="line-through">{sugg.suggested_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Beta Codes Tab - Unified System */}
        {activeTab === 'betacodes' && (
          <div className="space-y-6">
            {/* Actions Bar */}
            <div className="flex gap-3 flex-wrap">
              <Dialog open={showCreateCode} onOpenChange={setShowCreateCode}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full">
                    <Plus className="w-4 h-4 mr-2" /> Create Beta Code
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Beta Code</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="text-sm font-medium">Custom Code (optional)</label>
                      <Input 
                        placeholder="Leave empty for random code"
                        value={newCodeValue}
                        onChange={(e) => setNewCodeValue(e.target.value.toUpperCase())}
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">e.g., KINDFAMILY2025</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Notes (optional)</label>
                      <Input 
                        placeholder="Internal notes about this code"
                        value={newCodeNotes}
                        onChange={(e) => setNewCodeNotes(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleCreateBetaCode(!!newCodeValue.trim())}
                        className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white"
                      >
                        {newCodeValue.trim() ? 'Create Custom Code' : 'Generate Random Code'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Button 
                variant="outline" 
                onClick={handleMigrateKindFamily}
                className="border-2 border-black rounded-full"
              >
                Migrate KINDFAMILY2025
              </Button>
            </div>

            {/* Codes Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
                <div className="text-sm text-slate-600">Total Codes</div>
                <p className="text-3xl font-bold">{betaCodes.length}</p>
              </div>
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
                <div className="text-sm text-emerald-600">Available</div>
                <p className="text-3xl font-bold text-emerald-600">
                  {betaCodes.filter(c => !c.redeemed_by && c.is_active).length}
                </p>
              </div>
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
                <div className="text-sm text-indigo-600">Redeemed</div>
                <p className="text-3xl font-bold text-indigo-600">
                  {betaCodes.filter(c => c.redeemed_by).length}
                </p>
              </div>
            </div>

            {/* Codes List */}
            <div className="bg-white border-2 border-black rounded-xl shadow-brutal overflow-hidden">
              <div className="bg-slate-100 px-4 py-3 border-b-2 border-black">
                <h3 className="font-bold flex items-center gap-2">
                  <Key className="w-5 h-5" /> All Beta Codes
                </h3>
              </div>
              <div className="divide-y divide-slate-200">
                {betaCodes.map((code) => (
                  <div key={code.code_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <code className="font-mono font-bold text-lg bg-slate-100 px-3 py-1 rounded">
                          {code.code}
                        </code>
                        <button 
                          onClick={() => copyToClipboard(code.code)}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        {code.redeemed_by ? (
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                            Redeemed
                          </span>
                        ) : code.is_active ? (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                            Available
                          </span>
                        ) : (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                            Deactivated
                          </span>
                        )}
                        <span className="text-xs bg-slate-100 px-2 py-1 rounded-full">
                          {code.code_type}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Created: {new Date(code.created_at).toLocaleDateString()}
                        {code.notes && <span className="ml-2">• {code.notes}</span>}
                        {code.redeemed_at && (
                          <span className="ml-2">• Redeemed: {new Date(code.redeemed_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    {!code.redeemed_by && code.is_active && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeactivateCode(code.code_id, code.code)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {betaCodes.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    No beta codes created yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Beta Participants Tab */}
        {activeTab === 'participants' && (
          <div className="space-y-6">
            {/* Participants Summary */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
                <div className="text-sm text-slate-600">Total</div>
                <p className="text-3xl font-bold">{betaParticipants.length}</p>
              </div>
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
                <div className="text-sm text-amber-600">Onboarding</div>
                <p className="text-3xl font-bold text-amber-600">
                  {betaParticipants.filter(p => p.status === 'onboarding').length}
                </p>
              </div>
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
                <div className="text-sm text-indigo-600">Active</div>
                <p className="text-3xl font-bold text-indigo-600">
                  {betaParticipants.filter(p => p.status === 'active').length}
                </p>
              </div>
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-brutal">
                <div className="text-sm text-emerald-600">Completed</div>
                <p className="text-3xl font-bold text-emerald-600">
                  {betaParticipants.filter(p => p.status === 'completed').length}
                </p>
              </div>
            </div>

            {/* Participants List */}
            <div className="bg-white border-2 border-black rounded-xl shadow-brutal overflow-hidden">
              <div className="bg-slate-100 px-4 py-3 border-b-2 border-black">
                <h3 className="font-bold flex items-center gap-2">
                  <Activity className="w-5 h-5" /> Beta Participants Progress
                </h3>
              </div>
              <div className="divide-y divide-slate-200">
                {betaParticipants.map((p) => (
                  <div key={p.participant_id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="font-bold">{p.username}</span>
                        <span className="text-xs text-slate-500">{p.user_email}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          p.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          p.status === 'active' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {p.status}
                        </span>
                      </div>
                      <div className="text-sm text-slate-500">
                        Week {p.current_week} • {p.questions_completed}/60 questions
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            p.status === 'completed' ? 'bg-emerald-500' : 'bg-indigo-500'
                          }`}
                          style={{ width: `${p.completion_percentage || 0}%` }}
                        />
                      </div>
                      <span className="text-sm font-bold w-12 text-right">
                        {Math.round(p.completion_percentage || 0)}%
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-2">
                      Redeemed: {new Date(p.redeemed_at).toLocaleDateString()}
                      {p.last_survey_at && (
                        <span> • Last survey: {new Date(p.last_survey_at).toLocaleDateString()}</span>
                      )}
                      {p.reward_granted && (
                        <span className="text-emerald-600"> • 🎉 Year access granted!</span>
                      )}
                    </div>
                  </div>
                ))}
                {betaParticipants.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    No beta participants yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Audit Log Tab */}
        {activeTab === 'auditlog' && (
          <div className="bg-white border-2 border-black rounded-xl shadow-brutal overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 border-b-2 border-black">
              <h3 className="font-bold flex items-center gap-2">
                <FileText className="w-5 h-5" /> Beta System Audit Log
              </h3>
            </div>
            <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
              {betaAuditLogs.map((log) => (
                <div key={log.log_id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      log.event_type === 'code_created' ? 'bg-blue-100 text-blue-700' :
                      log.event_type === 'code_redeemed' ? 'bg-emerald-100 text-emerald-700' :
                      log.event_type === 'survey_milestone' ? 'bg-purple-100 text-purple-700' :
                      log.event_type === 'reward_granted' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {log.event_type.replace(/_/g, ' ')}
                    </span>
                    {log.beta_code && (
                      <code className="font-mono text-sm bg-slate-100 px-2 py-0.5 rounded">
                        {log.beta_code}
                      </code>
                    )}
                    <span className="text-xs text-slate-500">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <div className="text-xs text-slate-500 mt-1">
                      {Object.entries(log.details).map(([k, v]) => (
                        <span key={k} className="mr-3">{k}: {String(v)}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {betaAuditLogs.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No audit logs yet
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
